import React, { useEffect, useState, useRef, useContext } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled, { ThemeProvider } from "styled-components/macro";
import { use100vh } from "react-div-100vh";
import { get } from "lodash";
import { LayoutItem } from "simple-keyboard-layouts/build/interfaces";

import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import KeyboardLayouts from "simple-keyboard-layouts/build/index";

import mediaBreakpoints from "./utilities/media-sc-breakpoints";
import { BreakpointProvider } from "./utilities/media-js-breakpoints";
import ISO from "./utilities/languageISOConverter";

import { KeyboardContext } from "./utilities/virtual-keyboard";

import Configurator from "./pages/Configurator";
import Summary from "./pages/Summary";
import ErrorAlert from "./components/Alerts/ErrorAlert";
import { CREATE_SESSION } from "./store/actions/sessionActions";

import { AppLoader } from "./components/Loader/AppLoader";
import { RESOLUTION_CHANGED } from "./store/actions/imageActions";
import { REGISTER_ACTIVITY } from "./store/actions/timeoutActions";
import {
    getAppConfig,
    getConfigurationString,
    getFrontendSettings,
    getIsLoading,
    getShowVirtualKeyboard,
    getLanguage,
    getTouchScreen,
} from "./store/sagas/selectors";
import { goToModelSelector } from "./utilities/navigationUtils";

const AppContainer = styled.div`
    font-family: ${props => props.theme.fontFamily};
    color: ${props => props.theme.textColor};
    background: ${props => props.theme.background.color};
`;

const VirtualKeyboard = styled.div`
    width: 40%;
    ${({ theme }) => theme.bp.xxxl`
        width: 20%;
        transform: scale(2, 2);
        transform-origin: bottom center;
    `};
`;

const VirtualKeyboardContainer = styled.div`
    position: absolute;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    bottom: 0;
    z-index: 10000;
`;

const App = () => {
    const maxHeight: number | null = use100vh();

    const dispatch = useDispatch();

    const isLoading = useSelector(getIsLoading);
    const frontendSettings = useSelector(getFrontendSettings);
    const appConfig = useSelector(getAppConfig);
    const configurationString = useSelector(getConfigurationString);
    const showVirtualKeyboard = useSelector(getShowVirtualKeyboard);
    const language = useSelector(getLanguage);
    const isTouchScreen = useSelector(getTouchScreen);
    const [keyboardLayoutName, setkeyboardLayoutName] = useState("default");

    let languageISOValue = "english";
    if (language) {
        languageISOValue = ISO[language.toLocaleLowerCase()] || "english";
    }

    const layout = new KeyboardLayouts().get(languageISOValue) as LayoutItem;

    const [config, setConfig] = useState<any>({});

    const VirtualKeyboardContext = useContext(KeyboardContext);
    const virtualKeyboardRef = useRef<any>(null);

    const resetTimer = () => {
        dispatch({ type: REGISTER_ACTIVITY });
    };
    document.onclick = resetTimer;
    document.onkeydown = resetTimer;
    document.ontouchstart = resetTimer;
    document.onscroll = resetTimer;

    const setStyleProperties = (configProperties: any) => {
        const r = document.documentElement;
        r.style.setProperty("--primary-color", configProperties.colors.primaryColor);
        r.style.setProperty("--primary-hover-color", configProperties.colors.primaryHoverColor);
        r.style.setProperty("--primary-active-color", configProperties.colors.primaryActiveColor);
        r.style.setProperty("--light-hover-color", configProperties.colors.lightHoverColor);
        r.style.setProperty("--light-active-color", configProperties.colors.lightActiveColor);
    };

    useEffect(() => {
        const preventDefault = (event: Event) => event.preventDefault();

        if (isTouchScreen) {
            document.addEventListener("contextmenu", preventDefault);
        }

        return () => {
            if (isTouchScreen) {
                document.removeEventListener("contextmenu", preventDefault);
            }
        };
    }, [isTouchScreen]);

    useEffect(() => {
        const handleResize = () => {
            dispatch({ type: RESOLUTION_CHANGED });
        };
        window.addEventListener("resize", handleResize);

        const handleMessage = (event: MessageEvent) => {
            if (event.data.type) dispatch(event.data);
        };
        window.addEventListener("message", handleMessage);

        const searchParams = new URLSearchParams(window.location.search);
        const initialConfig = searchParams.get("config_string") || searchParams.get("encoded_config");
        if (initialConfig) {
            const configEncoding = searchParams.get("enc_type");
            const surfaceProductArticleNo = searchParams.get("surface_product");
            dispatch({ type: CREATE_SESSION, initialConfig, configEncoding, surfaceProductArticleNo });
        } else {
            goToModelSelector();
        }

        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("message", handleMessage);
        };
    }, [dispatch]);

    useEffect(() => {
        if (!appConfig) return;

        const breakpoints = { bp: mediaBreakpoints };
        setConfig({ ...appConfig.style, ...breakpoints });
        setStyleProperties(appConfig);
    }, [appConfig]);

    const updateConfigStringParam = (configString: string | null) => {
        const searchParams = new URLSearchParams(window.location.search);
        const serviceUrl = searchParams.get("service_url");
        searchParams.delete("config_string");
        searchParams.delete("encoded_config");
        searchParams.delete("enc_type");
        searchParams.delete("surface_product");
        searchParams.delete("service_url");
        const other = searchParams.toString();

        // Avoid URL encoding the config_string and service_url for easier copy/paste from the address bar.
        const parts = [];
        if (serviceUrl) parts.push(`service_url=${serviceUrl}`);
        if (other) parts.push(other);
        if (configString) parts.push(`config_string=${configString}`);

        const search = parts.length > 0 ? `?${parts.join("&")}` : "";
        window.history.pushState(null, "", `${window.location.pathname}${search}`);
    };

    // Remove config_string parameter from URL when enable_url_configuration_state is false.
    useEffect(() => {
        if (frontendSettings && !frontendSettings.enable_url_configuration_state) {
            updateConfigStringParam(null);
        }
    }, [frontendSettings]);

    // Update config_string parameter in URL when enable_url_configuration_state is true.
    useEffect(() => {
        if (configurationString && frontendSettings?.enable_url_configuration_state) {
            updateConfigStringParam(configurationString);
        }
    }, [configurationString, frontendSettings]);

    if (isLoading) {
        // Set the first loader to gray until styling.json has been loaded.
        const loaderColor = get(config, "appLoader.color", "#888");
        return (
            <AppLoader
                color={loaderColor}
                background={get(config, "appLoader.backgroundColor")}
                height={200}
                width={80}
            />
        );
    }

    const setKeyboardRef = (ref: any, startValue: string) => {
        virtualKeyboardRef.current = ref;
        virtualKeyboardRef.current.setInput(startValue);
    };

    const onKeyPress = (button: string) => {
        if (button === "{shift}" || button === "{lock}") {
            setkeyboardLayoutName(keyboardLayoutName === "default" ? "shift" : "default");
        }
    };

    const drawVirtualKeyboard = () => {
        return (
            <VirtualKeyboardContainer>
                <VirtualKeyboard>
                    <KeyboardContext.Provider value={VirtualKeyboardContext}>
                        {showVirtualKeyboard ? (
                            <Keyboard
                                keyboardRef={r => setKeyboardRef(r, VirtualKeyboardContext.content)}
                                layout={layout.layout}
                                layoutName={keyboardLayoutName}
                                onKeyPress={onKeyPress}
                                preventMouseDownDefault
                                useMouseEvents
                                onChange={(input: string) => {
                                    if (VirtualKeyboardContext.listener) {
                                        VirtualKeyboardContext.listener(input);
                                    }
                                }}
                            />
                        ) : null}
                    </KeyboardContext.Provider>
                </VirtualKeyboard>
            </VirtualKeyboardContainer>
        );
    };

    return (
        <BreakpointProvider>
            <ThemeProvider theme={config}>
                <BrowserRouter basename={window.location.pathname.replace(/\/summary$/, "")}>
                    <AppContainer style={{ height: maxHeight || "100vh" }}>
                        <Routes>
                            <Route path="/summary" element={<Summary />} />
                            <Route path="/" element={<Configurator />} />
                        </Routes>
                        {drawVirtualKeyboard()}
                    </AppContainer>
                </BrowserRouter>
                <ErrorAlert />
            </ThemeProvider>
        </BreakpointProvider>
    );
};

export default App;
